/**
 * Agents door (front-door proposal §03, door 3): talks to mia-flow-bff's proxy to
 * ai-foundry-workflows (routes/workflows.js). Mirrors AuthzService.js's shape —
 * getAuthorizedFetch() already attaches Authorization + x-mia-acl-context.
 *
 * This door only ever reads and triggers runs — it never authors a workflow
 * (no step editor, no schedule/webhook config). That stays ai-foundry-website's job.
 */

import config from '../config'
import { getAuthorizedFetch } from '../utils/fetchRegistry'

const bffBase = () => config.bffUrl || ''

const parseJson = async (response) => {
  if (!response.ok) {
    let details = ''
    try { details = (await response.json())?.error || (await response.text()) } catch { /* ignore */ }
    const error = new Error(details || `Request failed with status ${response.status}`)
    error.status = response.status
    throw error
  }
  if (response.status === 204 || response.status === 205) { return undefined }
  return response.json()
}

/**
 * GET /api/bff/workflows/runs — every run visible to the caller's org/tenant.
 * ai-foundry-workflows' own shape (src/api/runs.py:list_runs): `{items, metadata}`,
 * each item `{workflowId, runId, workflow, status, startedAt, closedAt, trigger}`.
 */
export const listRuns = async () => {
  const response = await getAuthorizedFetch()(`${bffBase()}/workflows/runs`, {
    headers: { Accept: 'application/json' },
  })
  const data = await parseJson(response)
  return Array.isArray(data?.items) ? data.items : []
}

/** GET /api/bff/workflows/runs/:id — a single run's current status/detail. */
export const getRun = async (runId) => {
  const response = await getAuthorizedFetch()(`${bffBase()}/workflows/runs/${encodeURIComponent(runId)}`, {
    headers: { Accept: 'application/json' },
  })
  return parseJson(response)
}

/**
 * POST /api/bff/workflows/runs — start a run.
 * `workflow` must already be a name the caller knows (no definitions-listing
 * endpoint exists yet — front-door proposal §07 decision 2, still open).
 */
export const startRun = async (workflow, inputs = {}) => {
  const response = await getAuthorizedFetch()(`${bffBase()}/workflows/runs`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify({ workflow, inputs }),
  })
  return parseJson(response)
}

/** POST /api/bff/workflows/runs/:id/cancel — stop a run the caller started. */
export const cancelRun = async (runId) => {
  const response = await getAuthorizedFetch()(`${bffBase()}/workflows/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  })
  return parseJson(response)
}
